import { LitElement, html } from './-global.min.js'

class HHLoadMore extends LitElement {
  static properties = {
    url: {
      type: String,
    },
    position: {
      type: String,
    },
    'target-class': {
      type: String,
    },
    currentPage: {
      type: Number,
      attribute: 'current-page',
    },
    totalPages: {
      type: Number,
      attribute: 'total-pages',
    },
  }

  static featuredLinkHandlerAttached = false

  constructor() {
    super()
    this.position = 'next'
    this.currentPage = 1
    this.totalPages = 1
    this.targetElement = null
    this.abortController = null
    this.preloadCache = new Map()
    this.preloadPromises = new Map()
    this.handleLoadMore = this.handleLoadMore.bind(this)
    this.handleLoadAll = this.handleLoadAll.bind(this)
    this.handlePreloadMore = this.handlePreloadMore.bind(this)
    this.handlePreloadAll = this.handlePreloadAll.bind(this)
  }

  /** Use property or attribute so preload works even when element is upgraded after DOM parse */
  getPreloadUrl() {
    return this.url ?? this.getAttribute('url') ?? ''
  }

  connectedCallback() {
    super.connectedCallback()
    this.loadMoreButton = this.querySelector('.js-load-more')
    this.loadMoreButton?.addEventListener('click', this.handleLoadMore)
    this.loadMoreButton?.addEventListener('mouseenter', this.handlePreloadMore)
    this.loadMoreButton?.addEventListener(
      'touchstart',
      this.handlePreloadMore,
      { passive: true }
    )

    this.loadAllButton = this.querySelector('.js-load-all')
    this.loadAllButton?.addEventListener('click', this.handleLoadAll)
    this.loadAllButton?.addEventListener('mouseenter', this.handlePreloadAll)
    this.loadAllButton?.addEventListener('touchstart', this.handlePreloadAll, {
      passive: true,
    })

    this.loadMoreContainer = document.querySelector('.js-load-more-container')

    const targetClass = this['target-class']
    if (targetClass) {
      this.targetElement = document.querySelector(targetClass)
    }

    this.handlePreloadMore()
    this.setupFeaturedLinkDelegation()
  }

  setupFeaturedLinkDelegation() {
    if (!HHLoadMore.featuredLinkHandlerAttached) {
      const handler = (e) => {
        const linkElement = e.target.closest('[data-featured-link]')
        if (linkElement) {
          const link = linkElement.getAttribute('data-featured-link')
          if (link) {
            window.location.href = link
          }
        }
      }
      document.addEventListener('click', handler)
      HHLoadMore.featuredLinkHandlerAttached = true
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.loadMoreButton?.removeEventListener('click', this.handleLoadMore)
    this.loadMoreButton?.removeEventListener(
      'mouseenter',
      this.handlePreloadMore
    )
    this.loadMoreButton?.removeEventListener(
      'touchstart',
      this.handlePreloadMore
    )

    this.loadAllButton?.removeEventListener('click', this.handleLoadAll)
    this.loadAllButton?.removeEventListener('mouseenter', this.handlePreloadAll)
    this.loadAllButton?.removeEventListener('touchstart', this.handlePreloadAll)

    this.preloadCache.clear()
    this.preloadPromises.clear()
  }

  preloadUrl(url) {
    if (this.preloadCache.has(url)) {
      return Promise.resolve(this.preloadCache.get(url))
    }

    if (this.preloadPromises.has(url)) {
      return this.preloadPromises.get(url)
    }

    const targetClass = this['target-class']
    const promise = fetch(url, { priority: 'low' })
      .then((res) => res.text())
      .then((data) => {
        const dataDom = new DOMParser().parseFromString(data, 'text/html')
        const newData = dataDom.querySelector(targetClass)

        // Create DocumentFragment with cloned nodes (ready to insert)
        const fragment = document.createDocumentFragment()
        if (newData) {
          Array.from(newData.children).forEach((child) => {
            fragment.appendChild(child.cloneNode(true))
          })
        }

        const result = { fragment, dataDom }
        this.preloadCache.set(url, result)
        this.preloadPromises.delete(url)
        return result
      })
      .catch((err) => {
        this.preloadPromises.delete(url)
        throw err
      })

    this.preloadPromises.set(url, promise)
    return promise
  }

  handlePreloadMore() {
    const url = this.getPreloadUrl()
    if (url) this.preloadUrl(url)
  }

  handlePreloadAll() {
    const baseUrl = this.getPreloadUrl()
    if (!baseUrl) return
    for (let page = this.currentPage + 1; page <= this.totalPages; page++) {
      const url = baseUrl.replace(/page=\d+/, `page=${page}`)
      this.preloadUrl(url)
    }
  }

  async handleLoadMore(e) {
    e?.preventDefault()
    try {
      const url = this.getPreloadUrl()
      const targetClass = this['target-class']

      this.setButtonLoading()

      if (this.position === 'next') {
        e && e.target.type === 'submit' ? this.setPageUrl(url) : null
      }

      const { fragment, dataDom } = await this.preloadUrl(url)
      this.preloadCache.delete(url)

      const targetEl = this.targetElement || document.querySelector(targetClass)

      if (this.position === 'previous') {
        targetEl.prepend(fragment)
      } else {
        targetEl.append(fragment)
      }

      targetEl
        .closest('collection-product-grid')
        ?.handleFeaturedContent({ direction: this.position })

      await this.rerenderComponent(dataDom)
      if (e && e.target.type === 'submit') {
        setTimeout(() => {
          const newData = dataDom.querySelector(targetClass)
          const firstNewProductCard = newData?.querySelector(
            '.product-grid__item:first-of-type .js-anchor'
          )
          const firstNewProductCardLink =
            firstNewProductCard?.getAttribute('href')
          if (firstNewProductCardLink) {
            const firstNewProductCardFocus = document.querySelector(
              `.js-product-grid-list .product-grid__item .js-anchor[href="${firstNewProductCardLink}"]`
            )
            firstNewProductCardFocus?.focus({ focusVisible: false })
          }
        }, 0)
      }
    } catch (error) {
      console.error(error)
    }
  }

  async handleLoadAll(e) {
    e?.preventDefault()

    this.abortController?.abort()
    this.abortController = new AbortController()
    const { signal } = this.abortController

    try {
      const targetClass = this['target-class']
      const targetEl = this.targetElement || document.querySelector(targetClass)
      this.setButtonLoading()

      const baseUrl = this.getPreloadUrl()
      const pagesToLoad = []
      for (let page = this.currentPage + 1; page <= this.totalPages; page++) {
        pagesToLoad.push({
          page,
          url: baseUrl.replace(/page=\d+/, `page=${page}`),
        })
      }

      if (pagesToLoad.length === 0) {
        this.loadMoreContainer?.classList.add('hidden')
        return
      }

      const totalPages = pagesToLoad.length
      const lastPageUrl = pagesToLoad[totalPages - 1].url
      const results = new Array(totalPages).fill(null)
      let nextToRender = 0
      let lastPageData = null

      // Batches and renders pages in order as fetches complete
      const renderPending = () => {
        if (signal.aborted) return

        // Collect all consecutive ready fragments
        const pendingFragments = []
        while (nextToRender < totalPages && results[nextToRender] !== null) {
          pendingFragments.push(results[nextToRender])
          nextToRender++
          this.currentPage++
        }

        if (pendingFragments.length === 0) return

        // Hide button on first render
        if (nextToRender === pendingFragments.length) {
          this.loadMoreContainer?.classList.add('hidden')
        }

        // Batch all fragments into one
        const batchedFragment = document.createDocumentFragment()
        for (const { fragment, dataDom, isLast } of pendingFragments) {
          batchedFragment.append(fragment)
          if (isLast) lastPageData = dataDom
        }

        // Single DOM read, single DOM write
        const scrollHeightBefore =
          this.position === 'previous' ? targetEl.scrollHeight : 0

        if (this.position === 'previous') {
          targetEl.prepend(batchedFragment)
          window.scrollBy(0, targetEl.scrollHeight - scrollHeightBefore)
        } else {
          targetEl.append(batchedFragment)
        }

        targetEl.closest('collection-product-grid')?.handleFeaturedContent()
      }

      const fetchPromises = pagesToLoad.map(async ({ url }, index) => {
        const { fragment, dataDom } = await this.preloadUrl(url)
        this.preloadCache.delete(url)

        if (signal.aborted) return

        const isLast = index === totalPages - 1
        results[index] = {
          fragment,
          dataDom: isLast ? dataDom : null,
          isLast,
        }
        renderPending()
      })

      await Promise.all(fetchPromises)
      if (signal.aborted) return

      this.url = lastPageUrl

      // Yield to browser before rerender
      await new Promise((resolve) => {
        if ('requestIdleCallback' in window) {
          requestIdleCallback(() => resolve(), { timeout: 100 })
        } else {
          setTimeout(() => resolve(), 0)
        }
      })

      if (signal.aborted) return

      if (lastPageData) {
        await this.rerenderComponent(lastPageData)
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Load all cancelled')
        this.resetButtonState()
        return
      }
      console.error(error)
      this.resetButtonState()
    }
  }

  cancelLoadAll() {
    this.abortController?.abort()
    this.resetButtonState()
  }

  setButtonLoading() {
    const buttonEl = this.querySelector('.js-load-more, .js-load-all')

    buttonEl?.classList.add('is-loading')
    buttonEl?.setAttribute('disabled', true)
  }

  resetButtonState() {
    const buttonEl = this.querySelector('.js-load-more, .js-load-all')

    buttonEl?.classList.remove('is-loading')
    buttonEl?.removeAttribute('disabled')
  }

  rerenderComponent(data) {
    if (!data) return
    const elId = `#${this.getAttribute('id')}`

    const newLoadMore = data.querySelector(elId)?.outerHTML || ''
    if (this.isConnected) {
      this.outerHTML = newLoadMore
    }
  }

  setPageUrl(url) {
    window.history.pushState({}, '', url)
  }

  render() {
    return html` <slot></slot> `
  }
}

customElements.define('hh-load-more', HHLoadMore)
